import { Theme } from '@radix-ui/themes'
import { message } from 'antd'
import { SetFieldAccessoryValue } from 'components/form/formTypes'
import { Coordinate } from 'components/form/inputs/coordinatesInput/helpers/coordinateTranslations'
import { getAllowableStressRecords } from 'features/indicationTriage/cards/ffsCalc/mawpSection/allowableStress/allowableStressValues'
import { FieldValueSource } from 'features/libra/ontology/ontologyFieldTypes'
import {
  ReadOnlySourceDisplay,
  SourceInput,
} from 'features/libra/stepComponents/conditionalForm/conditionalFormSection/SourceInput'
import {
  FFS_FIELD_OVERRIDES,
  PRESSURE_COEFFICIENT_DATAKEY,
  YIELD_STRENGTH_DATAKEY,
} from 'features/libra/stepComponents/ffs/ffsAssetModelFieldOverrides'
import { isFieldHiddenInFfs } from 'features/libra/stepComponents/ffs/ffsHiddenAssetModelFields'
import { fieldSupportsProvenance } from 'features/libra/stepComponents/ffs/ffsProvenanceFields'
import { MaterialStressPickerAccessory } from 'features/libra/stepComponents/ffs/MaterialStressPickerAccessory'
import { PressureCoefficientPickerAccessory } from 'features/libra/stepComponents/ffs/PressureCoefficientPickerAccessory'
import { useFfsInputProvenance } from 'features/libra/stepComponents/ffs/useFfsInputProvenance'
import { useFfsWorkspaceState } from 'features/libra/workspace/ffs/FfsWorkspaceStateContext'
import { EditGeckoRichAssetModel } from 'features/rightDrawer/drawerStates/crudWorkflows/editRichAssetModel/EditGeckoRichAssetModel'
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { GeckoAssetModel, GeckoComponent, GeckoFeature, GeckoUnit } from 'types'

/** Indents the inline source picker under its field (accessory owns its layout). */
const SourceAccessory = styled.div`
  padding-left: 1.5rem;
`

interface FfsAssetModelEditorProps {
  assetModel: GeckoAssetModel
  component: GeckoComponent
  unit: GeckoUnit
  /**
   * Invoked after the editor saves a write. Pass
   * `useFfsTicketContext().forceRefresh` so downstream consumers
   * (viz pane, validation hooks) re-fetch.
   */
  refreshData: () => Promise<void>
}

/**
 * Inline-embedded asset model editor wired to the FFS workspace state.
 *
 * Owns the workspace-coupling glue that was previously copy-pasted across
 * `FfsAssetModelStep` and `FfsMinimumThicknessStep`:
 *  - `selectedFeatureId` is derived from `useFfsWorkspaceState().selection`
 *  - `onFeatureSelected` resolves the feature id against `assetModel`
 *    (which the workspace context doesn't own) and writes the matched
 *    `GeckoFeature` back to selection
 *  - `displayCoordinateUpdatesApi` + `highlightCoordinate` bind the
 *    component's tags into each `coordinateDisplay` call so markup layers
 *    are scoped correctly
 *  - Radix `<Theme>` wraps the editor so its per-feature delete
 *    `@radix-ui/themes` Popover has a styled context (without this the
 *    Confirm button renders as an empty dark rectangle)
 */
export const FfsAssetModelEditor: FC<FfsAssetModelEditorProps> = ({
  assetModel,
  component,
  unit,
  refreshData,
}) => {
  const { selection, setSelection, pointAndClickApi, coordinateDisplay } =
    useFfsWorkspaceState()

  // Inline per-field source/citation picker. Keyed by the field's `dataKey`
  // (== the provenance `jsonField`); every field the shared editor renders gets
  // one except the explicit opt-outs in `fieldSupportsProvenance`.
  // `component.id` equals the ticket `componentId` the Min Thickness read side
  // uses, so sources are shared across both steps.
  const { readOnly, getFieldSource, saveFieldSources } = useFfsInputProvenance(
    component.id
  )

  // Source edits are STAGED locally (never auto-saved) and flushed to the ticket
  // only when the editor's Save button persists (see `handleSaved`). Reset on
  // component change so a tab switch doesn't leak edits to another component.
  const [staged, setStaged] = useState<Record<string, FieldValueSource | null>>(
    {}
  )
  useEffect(() => setStaged({}), [component.id])

  // Inline source/citation picker: every provenant field gets one.
  const renderSourceAccessory = useCallback(
    (dataKey: string): ReactNode => {
      if (!fieldSupportsProvenance(dataKey)) return null
      // Staged edit wins over the persisted source; a staged `null` (cleared)
      // must NOT fall back to the persisted value, hence the `in` check.
      const value =
        dataKey in staged ? staged[dataKey] : getFieldSource(dataKey)
      if (readOnly) {
        return value ? (
          <SourceAccessory>
            <ReadOnlySourceDisplay source={value} />
          </SourceAccessory>
        ) : null
      }
      return (
        <SourceAccessory>
          <SourceInput
            source={value}
            onSourceChange={(source) =>
              setStaged((prev) => ({ ...prev, [dataKey]: source }))
            }
          />
        </SourceAccessory>
      )
    },
    [readOnly, getFieldSource, staged]
  )

  // Allowable-stress dataset for THIS component's type (piping component types →
  // B31.3 Table A-1 catalog; other types → null). Null hides the picker button.
  const allowableStressRecords = useMemo(
    () =>
      getAllowableStressRecords(
        assetModel.data.components.get(component.id)?.type
      ),
    [assetModel, component.id]
  )

  const renderFieldAccessory = useCallback(
    (dataKey: string, setFieldValue: SetFieldAccessoryValue): ReactNode => {
      const sourceNode = renderSourceAccessory(dataKey)
      // Allowable-stress lookup table beside the S (yield_strength) input. Edit-only:
      // it WRITES a value, meaningless in read mode. Renders in addition to (not
      // instead of) the source picker. The accessory hides itself when the resolved
      // dataset is null (component type has no material catalog yet).
      const stressNode =
        dataKey === YIELD_STRENGTH_DATAKEY && !readOnly ? (
          <MaterialStressPickerAccessory
            setFieldValue={setFieldValue}
            records={allowableStressRecords}
          />
        ) : null

      // Coefficient-Y automation beside the Y (pressure_coefficient) input. Edit-only (it
      // WRITES a value). Gated to piping via the same signal as the S picker.
      const yNode =
        dataKey === PRESSURE_COEFFICIENT_DATAKEY &&
        !readOnly &&
        allowableStressRecords !== null ? (
          <PressureCoefficientPickerAccessory
            setFieldValue={setFieldValue}
            componentId={component.id}
          />
        ) : null

      if (!sourceNode && !stressNode && !yNode) return null
      return (
        <>
          {sourceNode}
          {stressNode}
          {yNode}
        </>
      )
    },
    [renderSourceAccessory, readOnly, allowableStressRecords, component.id]
  )

  // Flush staged source edits when the editor's VALUE save persists. Invoked
  // (and awaited) by the shared editor's save handler AFTER the value PUT
  // resolves but BEFORE its refresh, so consumers never read a half-applied
  // value/citation pair. The whole batch lands in one ticket write (see
  // `saveFieldSources`) so it can't partially succeed.
  //
  // The enqueue is owned by the TicketWriter mounted ABOVE this step, so the
  // write completes even though the `isSaving` transition remounts this editor
  // mid-flush; `staged` is cleared only on a confirmed success. On failure the
  // value already saved, so we surface a DISTINCT message (not the value-save
  // error) prompting the user to re-select the source and save again — `staged`
  // is local state and is NOT guaranteed to survive the remount, so we don't
  // rely on an automatic retry.
  const handleSaved = useCallback(async () => {
    if (Object.keys(staged).length === 0) return
    try {
      await saveFieldSources(staged)
      setStaged({})
    } catch {
      message.error(
        'The value was saved, but its source could not be saved. ' +
          'Please re-select the source and save again.'
      )
    }
  }, [staged, saveFieldSources])

  const selectedFeatureId =
    selection instanceof GeckoFeature ? selection.id : undefined

  const onFeatureSelected = useCallback(
    (
      updater:
        | string
        | undefined
        | ((prev: string | undefined) => string | undefined)
    ) => {
      const nextId =
        typeof updater === 'function' ? updater(selectedFeatureId) : updater
      if (!nextId) {
        setSelection(undefined)
        return
      }
      const cm = assetModel.data.components.get(component.id)
      const feature = cm?.features.get(nextId)
      if (feature) setSelection(feature)
    },
    [assetModel, component.id, selectedFeatureId, setSelection]
  )

  const displayCoordinateUpdatesApi = useMemo(
    () => ({
      display: (coords: ReadonlyArray<Coordinate>) =>
        coordinateDisplay.display(coords, component.tags),
      clear: () => coordinateDisplay.clear(),
    }),
    [component.tags, coordinateDisplay]
  )

  const highlightCoordinate = useCallback(
    (coord: Coordinate | null) => {
      if (coord === null) coordinateDisplay.clear()
      else coordinateDisplay.display([coord], component.tags)
    },
    [component.tags, coordinateDisplay]
  )

  // Cleanup on unmount: clear any coordinate overlay this editor pushed to
  // the viz pane, and exit point-selection mode if we left it active.
  // Without this, closing the min-thickness drawer can leave stale dots in
  // the viz or trap the user in select-point cursor mode on the next step.
  const cleanupRef = useRef({ pointAndClickApi, coordinateDisplay })
  cleanupRef.current = { pointAndClickApi, coordinateDisplay }
  useEffect(() => {
    return () => {
      const latest = cleanupRef.current
      latest.coordinateDisplay.clear()
      if (latest.pointAndClickApi.isInPointSelectionMode) {
        latest.pointAndClickApi.exitPointSelectionMode()
      }
    }
  }, [])

  return (
    <Theme asChild hasBackground={false} appearance='inherit'>
      <div>
        <EditGeckoRichAssetModel
          assetModel={assetModel}
          component={component}
          unit={unit}
          refreshData={refreshData}
          selectedFeatureId={selectedFeatureId}
          onFeatureSelected={onFeatureSelected}
          pointAndClickApi={pointAndClickApi}
          highlightCoordinate={highlightCoordinate}
          displayCoordinateUpdatesApi={displayCoordinateUpdatesApi}
          renderFieldAccessory={renderFieldAccessory}
          hideField={isFieldHiddenInFfs}
          fieldOverrides={FFS_FIELD_OVERRIDES}
          onSaved={handleSaved}
        />
      </div>
    </Theme>
  )
}
